import { Navbar, Footer } from './LandingPage';
import { WanderfulHero } from './WanderfulHero';
import { ArrowLeft, AlertCircle, TrendingUp } from 'lucide-react';

const CaseStudyPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WanderfulHero
        title="Anonymized Case Study"
        subtitle="How Parakh AI identified critical grading discrepancies in a batch of 5,000 previously marked answer sheets."
        cta1Text="Book a Demo"
      />

      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <a href="/resources" className="inline-flex items-center gap-2 text-gray-500 hover:text-black transition-colors font-bold uppercase text-xs tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Back to Resources
        </a>
      </div>

      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg prose-slate max-w-none">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-8">The Challenge</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-12">
              A premier coaching institute in New Delhi, specializing in UPSC preparations, faced a significant challenge: consistency. With over 20 senior faculty members marking thousands of answer sheets weekly, the management suspected that "evaluator fatigue" and "subjective bias" were leading to inconsistent results.
            </p>

            <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 mb-16 border border-gray-100">
               <h3 className="text-xl sm:text-2xl font-bold text-black mb-6">The Experiment</h3>
               <p className="text-gray-600 mb-6">
                 We took 5,000 answer sheets that had already been marked by the faculty and fed them into Parakh AI. The goal wasn't just to see if the marks matched, but to identify *why* they might differ.
               </p>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                 <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm">
                   <div className="text-4xl font-bold text-black mb-2">5,000</div>
                   <div className="text-sm text-gray-500 font-bold uppercase">Sheets Evaluated</div>
                 </div>
                 <div className="bg-white p-6 rounded-2xl shadow-sm">
                   <div className="text-4xl font-bold text-black mb-2">20+</div>
                   <div className="text-sm text-gray-500 font-bold uppercase">Human Faculty</div>
                 </div>
               </div>
            </div>

            <h2 className="text-4xl font-bold text-black mb-8">The Findings</h2>
            <div className="space-y-8 mb-16">
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-red-50 flex-shrink-0 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-black mb-2">Missed Answers</h4>
                  <p className="text-gray-600">In 50 instances, human evaluators had completely skipped evaluating a full page or a sub-part of a question, awarding 0 marks where the student had actually written an answer.</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-orange-50 flex-shrink-0 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-black mb-2">Calculation Errors</h4>
                  <p className="text-gray-600">150 sheets had simple addition errors in the final score. Even the best teachers make mistakes when marking their 100th paper of the day.</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex-shrink-0 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-black mb-2">Subjective Drift</h4>
                  <p className="text-gray-600">We found that the same faculty member awarded 20% higher marks for similar quality answers in the morning compared to late evening papers.</p>
                </div>
              </div>
            </div>

            <div className="bg-black text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16 mb-16">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6">The Result: Fairer for Students</h3>
              <p className="text-white/70 text-lg mb-8">
                By implementing Parakh AI, the institute was able to correct these errors before results were published. This not only saved them from student grievances but also boosted student trust in the evaluation process.
              </p>
              <div className="flex flex-wrap gap-4">
                 <div className="bg-white/10 px-6 py-3 rounded-full border border-white/20 text-sm font-bold">100% Accuracy in Totalling</div>
                 <div className="bg-white/10 px-6 py-3 rounded-full border border-white/20 text-sm font-bold">Zero Missed Answers</div>
                 <div className="bg-white/10 px-6 py-3 rounded-full border border-white/20 text-sm font-bold">Auditable Evaluations</div>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-8">Conclusion</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-12">
              AI isn't here to replace teachers, but to act as their most diligent assistant. By handling the drudgery of checking for missed answers and calculating totals, Parakh AI allows teachers to focus on providing qualitative feedback that helps students grow.
            </p>
          </div>
        </div>
      </section>

     

      <Footer />
    </div>
  );
};

export default CaseStudyPage;
